import React, { useEffect, useMemo } from 'react';
import { Button, Stack } from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { Listing, useListing } from 'components/shared/TableListing';
import { getSubsidyTableColumns, subsidyTableHeaderColumns } from '../../constants';
import { SubsidyTableRow } from 'modules/subsidyCalculator/types';

interface Props {
  subsidyPercent: number;
  dnetCoefficient: number;
  onRequestSubsidy: (row: SubsidyTableRow) => void;
  isSubmitted: boolean;
  isRequesting: boolean;
  onShowCertificate?: () => void;
  existingValues?: Pick<SubsidyTableRow, 'dnet' | 'financeAmount' | 'approvedSubsidyAmount'>;
}

export const SubsidyTable = ({
  subsidyPercent,
  dnetCoefficient,
  onRequestSubsidy,
  isSubmitted,
  isRequesting,
  onShowCertificate,
  existingValues,
}: Props) => {
  const { form, tableData } = useListing<SubsidyTableRow>(
    {
      headerColumns: subsidyTableHeaderColumns,
      bodyFields: [
        {
          dnet: 0,
          dnetCoefficient,
          financeAmount: 0,
          subsidyPercent,
          approvedSubsidyAmount: 0,
        },
      ],
    },
    { shouldUnregister: false }
  );

  useEffect(() => {
    if (existingValues) {
      form.reset({
        bodyFields: [
          {
            dnet: existingValues.dnet,
            dnetCoefficient,
            financeAmount: existingValues.financeAmount,
            subsidyPercent,
            approvedSubsidyAmount: existingValues.approvedSubsidyAmount,
          },
        ],
      });
    }
  }, [existingValues, dnetCoefficient, form, subsidyPercent]);

  const columns = useMemo(
    () =>
      getSubsidyTableColumns({
        control: form.control,
        subsidyPercent,
        dnetCoefficient,
        disabled: isSubmitted,
        storedApprovedSubsidyAmount: existingValues?.approvedSubsidyAmount,
      }),
    [form.control, subsidyPercent, dnetCoefficient, isSubmitted, existingValues?.approvedSubsidyAmount]
  );

  const handleRequestSubsidy = () => {
    const row = form.getValues('bodyFields.0');
    onRequestSubsidy(row);
  };

  return (
    <FormProvider {...form}>
      <Listing tableData={tableData} columns={columns} />
      <Stack direction="row" justifyContent="flex-end" gap="0.5rem" mt="0.5rem">
        {isSubmitted && onShowCertificate && (
          <Button variant="outlined" size="small" onClick={onShowCertificate}>
            Show Certificate
          </Button>
        )}
        <Button variant="contained" onClick={handleRequestSubsidy} disabled={isSubmitted || isRequesting}>
          {isSubmitted ? 'Subsidy Requested' : isRequesting ? 'Requesting...' : 'Request Subsidy'}
        </Button>
      </Stack>
    </FormProvider>
  );
};
